import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { dfsDashboardAPI, cashAPI, cashAccountsAPI, accountsAPI } from '../../lib/api';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Landmark,
  DollarSign,
  Wallet,
  Users,
  CreditCard,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ArrowUpCircle,
  Clock,
  RefreshCw,
  Calendar,
  Briefcase,
  Package,
  UserCheck,
  ChevronRight,
  ChevronDown,
  Activity,
  PieChart,
  Building2,
  Smartphone,
  PiggyBank,
  Plus,
  X,
  History,
} from 'lucide-react';

// Spells out the currency code ("USD $1,500.00" / "LRD $10,000") instead
// of relying on "$" vs "L$" alone to distinguish them.
const formatCurrency = (value, currency = 'USD') => {
  const amount = currency === 'LRD'
    ? new Intl.NumberFormat('en-US').format(value || 0)
    : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
  return `${currency} $${amount}`;
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value || 0);

const ACCOUNT_TYPE_CONFIG = {
  teller: { icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Tellers' },
  bank: { icon: Building2, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Bank Accounts' },
  petty_cash: { icon: PiggyBank, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Petty Cash' },
  mobile_money: { icon: Smartphone, color: 'text-green-600', bg: 'bg-green-100', label: 'Mobile Money' },
  coa: { icon: DollarSign, color: 'text-slate-600', bg: 'bg-slate-100', label: 'Chart of Accounts' },
};

function StatCard({ title, value, subValue, icon: Icon, iconColor, trend, link }) {
  const content = (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subValue && <p className="text-sm text-muted-foreground mt-0.5">{subValue}</p>}
          {trend && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              <span>{Math.abs(trend)}% vs yesterday</span>
            </div>
          )}
        </div>
        <div className={`p-2 rounded-lg ${iconColor || 'bg-primary/10'}`}>
          <Icon className={`h-5 w-5 ${iconColor?.includes('bg-') ? 'text-white' : 'text-primary'}`} />
        </div>
      </div>
      {link && (
        <div className="mt-3 pt-3 border-t border-border">
          <span className="text-xs text-primary flex items-center gap-1 hover:underline">
            View Details <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      )}
    </div>
  );

  return link ? <Link to={link}>{content}</Link> : content;
}

function TransactionRow({ transaction }) {
  const isInflow = transaction.subtype?.includes('add') || 
                   transaction.subtype?.includes('deposit') || 
                   transaction.subtype === 'cash_deposit';
  
  return (
    <tr className="border-b border-border/50 hover:bg-muted/30">
      <td className="py-2 px-3 text-xs text-muted-foreground">
        {transaction.performed_at ? new Date(transaction.performed_at).toLocaleTimeString() : '-'}
      </td>
      <td className="py-2 px-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
          transaction.type === 'allocation' ? 'bg-primary/10 text-primary' : 'bg-blue-100 text-blue-700'
        }`}>
          {transaction.type === 'allocation' ? <CreditCard className="h-3 w-3" /> : <Users className="h-3 w-3" />}
          {transaction.type}
        </span>
      </td>
      <td className="py-2 px-3 text-sm">{transaction.description}</td>
      <td className="py-2 px-3 text-right font-mono">
        <span className={isInflow ? 'text-green-600' : 'text-red-600'}>
          {isInflow ? '+' : '-'}{formatCurrency(transaction.amount, transaction.currency)}
        </span>
      </td>
      <td className="py-2 px-3 text-sm text-muted-foreground">{transaction.performed_by}</td>
      <td className="py-2 px-3 text-sm text-muted-foreground">{transaction.reference || '-'}</td>
    </tr>
  );
}

function TellerPerformanceCard({ teller }) {
  return (
    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${teller.has_active_session ? 'bg-green-500' : 'bg-gray-400'}`} />
        <div>
          <p className="font-medium text-sm">{teller.code}</p>
          <p className="text-xs text-muted-foreground">{teller.name}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-medium">{teller.transaction_count} txns</p>
        <p className="text-xs text-green-600">{formatCurrency(teller.usd_balance, 'USD')}</p>
      </div>
    </div>
  );
}

// Add Cash Account Modal
function AddCashAccountModal({ isOpen, onClose, onSuccess, companyId, defaultType = 'bank' }) {
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState(defaultType);
  const [currency, setCurrency] = useState('USD');
  const [initialBalance, setInitialBalance] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [coaAccountId, setCoaAccountId] = useState('');

  const { data: coaAccounts = [] } = useQuery({
    queryKey: ['coa-cash-accounts', companyId],
    queryFn: () => accountsAPI.getAll(companyId).then(res => 
      res.data.filter(a => a.account_type === 'asset')
    ),
    enabled: isOpen && !!companyId,
  });

  const mutation = useMutation({
    mutationFn: (data) => cashAccountsAPI.create(data),
    onSuccess: () => {
      toast.success('Account created successfully');
      onSuccess();
      onClose();
      setName('');
      setInitialBalance('');
      setInstitutionName('');
      setAccountNumber('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name) {
      toast.error('Please enter account name');
      return;
    }
    mutation.mutate({
      company_id: companyId,
      name,
      account_type: accountType,
      currency,
      initial_balance: parseFloat(initialBalance) || 0,
      institution_name: institutionName || undefined,
      account_number: accountNumber || undefined,
      coa_account_id: coaAccountId || undefined,
    });
  };

  if (!isOpen) return null;

  const config = ACCOUNT_TYPE_CONFIG[accountType];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              {config && <config.icon className={`h-5 w-5 ${config.color}`} />}
              Add {config?.label || 'Account'}
            </h3>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Account Type</label>
              <select
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              >
                <option value="bank">Bank Account</option>
                <option value="petty_cash">Petty Cash</option>
                <option value="mobile_money">Mobile Money</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Account Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={accountType === 'bank' ? 'e.g., Ecobank Operating' : accountType === 'mobile_money' ? 'e.g., Orange Money' : 'e.g., Office Petty Cash'}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              />
            </div>

            {(accountType === 'bank' || accountType === 'mobile_money') && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  {accountType === 'bank' ? 'Bank Name' : 'Provider'}
                </label>
                <input
                  type="text"
                  value={institutionName}
                  onChange={(e) => setInstitutionName(e.target.value)}
                  placeholder={accountType === 'bank' ? 'e.g., Ecobank, LBDI' : 'e.g., Orange Money, MTN'}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                />
              </div>
            )}

            {accountType === 'bank' && (
              <div>
                <label className="block text-sm font-medium mb-1">Account Number</label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="e.g., 0012345678"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                >
                  <option value="USD">USD ($)</option>
                  <option value="LRD">LRD (L$)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Initial Balance</label>
                <input
                  type="number"
                  value={initialBalance}
                  onChange={(e) => setInitialBalance(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Link to COA</label>
              <select
                value={coaAccountId}
                onChange={(e) => setCoaAccountId(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              >
                <option value="">Select account (optional)</option>
                {coaAccounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-muted">
                Cancel
              </button>
              <button type="submit" disabled={mutation.isPending} className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50">
                {mutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Cash Position Details Panel (inline expandable)
function CashPositionDetails({ companyId, onAddAccount }) {
  const queryClient = useQueryClient();

  const { data: cashPosition, isLoading } = useQuery({
    queryKey: ['cash-position', companyId],
    queryFn: () => cashAPI.getCashPosition(companyId).then(res => res.data),
    enabled: !!companyId,
  });

  const { data: cashAccounts = [] } = useQuery({
    queryKey: ['cash-accounts', companyId],
    queryFn: () => cashAccountsAPI.getAll(companyId).then(res => res.data),
    enabled: !!companyId,
  });

  if (isLoading) {
    return <div className="p-4 text-center text-muted-foreground">Loading cash position...</div>;
  }

  const categories = [
    { type: 'teller', label: 'Tellers' },
    { type: 'bank', label: 'Bank Accounts' },
    { type: 'petty_cash', label: 'Petty Cash' },
    { type: 'mobile_money', label: 'Mobile Money' },
    { type: 'coa', label: 'Chart of Accounts' },
  ];

  return (
    <div className="space-y-3 mt-4 border-t border-border pt-4">
      {categories.map((cat) => {
        const config = ACCOUNT_TYPE_CONFIG[cat.type];
        const Icon = config?.icon || Wallet;
        const accounts = cashPosition?.sources?.filter(s => s.type === cat.type) || [];
        const totalUsd = accounts.reduce((sum, a) => sum + (a.usd || 0), 0);
        const totalLrd = accounts.reduce((sum, a) => sum + (a.lrd || 0), 0);
        const isCashAccountType = ['bank', 'petty_cash', 'mobile_money'].includes(cat.type);

        return (
          <div key={cat.type} className="bg-muted/30 rounded-lg overflow-hidden">
            <div className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`p-1.5 ${config?.bg} rounded`}>
                  <Icon className={`h-4 w-4 ${config?.color}`} />
                </div>
                <div>
                  <span className="font-medium text-sm">{cat.label}</span>
                  <span className="text-xs text-muted-foreground ml-2">({accounts.length})</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className="text-sm font-semibold text-green-600">{formatCurrency(totalUsd, 'USD')}</span>
                  {totalLrd > 0 && <span className="text-xs text-blue-600 ml-2">{formatCurrency(totalLrd, 'LRD')}</span>}
                </div>
                {isCashAccountType && (
                  <button
                    onClick={() => onAddAccount(cat.type)}
                    className="p-1 hover:bg-muted rounded text-primary"
                    title={`Add ${cat.label}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            {accounts.length > 0 && (
              <div className="border-t border-border/50 divide-y divide-border/30">
                {accounts.map((account) => (
                  <div key={account.id} className="px-3 py-2 flex items-center justify-between text-sm hover:bg-muted/50">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">•</span>
                      <span>{account.name}</span>
                      {account.institution_name && (
                        <span className="text-xs text-muted-foreground">({account.institution_name})</span>
                      )}
                    </div>
                    <span className={`font-mono ${account.currency === 'USD' ? 'text-green-600' : 'text-blue-600'}`}>
                      {formatCurrency(account.balance, account.currency)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {accounts.length === 0 && isCashAccountType && (
              <div className="px-3 py-2 border-t border-border/50 text-center">
                <button
                  onClick={() => onAddAccount(cat.type)}
                  className="text-xs text-primary hover:underline"
                >
                  Add first {cat.label.toLowerCase()}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function DFSDashboard() {
  const { getDFSCompany } = useCompany();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCashDetails, setShowCashDetails] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalType, setAddModalType] = useState('bank');
  
  const dfsCompany = getDFSCompany ? getDFSCompany() : null;
  const companyId = dfsCompany?.id;

  const { data: dashboard, isLoading, refetch } = useQuery({
    queryKey: ['dfs-dashboard', companyId, selectedDate],
    queryFn: () => dfsDashboardAPI.getStats(companyId, selectedDate).then(res => res.data),
    enabled: !!companyId,
    refetchInterval: 60000, // Auto-refresh every minute
  });

  const handleAddAccount = (type) => {
    setAddModalType(type);
    setShowAddModal(true);
  };

  const handleRefresh = () => {
    refetch();
    queryClient.invalidateQueries(['cash-position', companyId]);
    queryClient.invalidateQueries(['cash-accounts', companyId]);
  };

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">DFS Company not found. Please select DFS from the subsidiary list.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const daily = dashboard?.daily_activity || {};
  const loans = dashboard?.loan_portfolio || {};
  const customers = dashboard?.customer_metrics || {};
  const stockAlerts = dashboard?.stock_alerts || {};
  const transactions = dashboard?.transactions || [];
  const tellerPerf = dashboard?.teller_performance || [];

  return (
    <div className="space-y-6" data-testid="dfs-dashboard-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Landmark className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">DEA Financial Services</h1>
              <p className="text-muted-foreground text-sm">Subsidiary Dashboard Overview</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-1.5 bg-background border border-border rounded-md text-sm"
              data-testid="date-picker"
            />
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-md hover:bg-muted text-sm"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Cash Position KPIs */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Cash Position
          </h2>
          <button
            onClick={() => setShowCashDetails(!showCashDetails)}
            className="flex items-center gap-1 text-sm text-primary hover:underline"
            data-testid="view-cash-details-btn"
          >
            {showCashDetails ? 'Hide Details' : 'View Details'}
            {showCashDetails ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
            <p className="text-sm text-muted-foreground">Total Cash (USD)</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_cash_usd, 'USD')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Teller: {formatCurrency(summary.teller_cash_usd)} | COA: {formatCurrency(summary.coa_cash_usd)}
            </p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <p className="text-sm text-muted-foreground">Total Cash (LRD)</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(summary.total_cash_lrd, 'LRD')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Teller: {formatCurrency(summary.teller_cash_lrd, 'LRD')} | COA: {formatCurrency(summary.coa_cash_lrd, 'LRD')}
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
            <p className="text-sm text-muted-foreground">Active Tellers</p>
            <p className="text-2xl font-bold text-purple-600">{summary.active_tellers || 0} / {summary.total_tellers || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Sessions open</p>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
            <p className="text-sm text-muted-foreground">Pending Allocations</p>
            <p className="text-2xl font-bold text-yellow-600">{summary.pending_allocations_count || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">{formatCurrency(summary.pending_allocations_usd)}</p>
          </div>
        </div>
        
        {/* Expandable Cash Position Details */}
        {showCashDetails && (
          <CashPositionDetails companyId={companyId} onAddAccount={handleAddAccount} />
        )}
      </div>

      {/* Daily Activity & Loan Portfolio */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Daily Activity */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Today's Activity
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUpCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-muted-foreground">Allocated to Tellers</span>
              </div>
              <p className="text-lg font-bold text-red-600">{formatCurrency(daily.allocated_out_usd, 'USD')}</p>
              <p className="text-sm text-red-600">{formatCurrency(daily.allocated_out_lrd, 'LRD')}</p>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Total Transactions Today</p>
              <p className="text-2xl font-bold">{daily.transaction_count || 0}</p>
            </div>
          </div>
        </div>

        {/* Loan Portfolio */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-primary" />
            Loan Portfolio
            <Link to="/dfs/loans" className="ml-auto text-xs text-primary hover:underline flex items-center gap-1">
              View All <ChevronRight className="h-3 w-3" />
            </Link>
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <p className="text-2xl font-bold text-primary">{loans.total_loans || 0}</p>
              <p className="text-xs text-muted-foreground">Total Loans</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{loans.active_loans || 0}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{loans.pending_approval || 0}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Total Disbursed</p>
              <p className="text-lg font-bold">{formatCurrency(loans.total_disbursed_usd, 'USD')}</p>
              {loans.total_disbursed_lrd > 0 && (
                <p className="text-xs font-medium text-blue-600">{formatCurrency(loans.total_disbursed_lrd, 'LRD')}</p>
              )}
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Outstanding</p>
              <p className="text-lg font-bold">{formatCurrency(loans.total_outstanding_usd, 'USD')}</p>
              {loans.total_outstanding_lrd > 0 && (
                <p className="text-xs font-medium text-blue-600">{formatCurrency(loans.total_outstanding_lrd, 'LRD')}</p>
              )}
            </div>
          </div>
          {loans.overdue_loans > 0 && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-700">{loans.overdue_loans} overdue loan(s) require attention</span>
            </div>
          )}
        </div>
      </div>

      {/* Customer Metrics & Stock Alerts */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Customer Metrics */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-primary" />
            Customer Metrics
            <Link to="/dfs/customers" className="ml-auto text-xs text-primary hover:underline flex items-center gap-1">
              View All <ChevronRight className="h-3 w-3" />
            </Link>
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Customers</span>
              <span className="font-bold">{formatNumber(customers.total_customers)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Active Customers</span>
              <span className="font-bold text-green-600">{formatNumber(customers.active_customers)}</span>
            </div>
            <div className="border-t border-border pt-3 mt-3">
              <p className="text-sm text-muted-foreground mb-1">Total Deposits</p>
              <p className="font-bold text-green-600">{formatCurrency(customers.total_deposits_usd, 'USD')}</p>
              <p className="text-sm text-blue-600">{formatCurrency(customers.total_deposits_lrd, 'LRD')}</p>
            </div>
          </div>
        </div>

        {/* Stock Alerts */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" />
            Stock Alerts
            <Link to="/dfs/stock" className="ml-auto text-xs text-primary hover:underline flex items-center gap-1">
              Manage <ChevronRight className="h-3 w-3" />
            </Link>
          </h3>
          {stockAlerts.low_stock_count > 0 ? (
            <div className="space-y-2">
              <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm text-yellow-700">{stockAlerts.low_stock_count} item(s) running low</span>
              </div>
              {stockAlerts.items?.slice(0, 3).map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                  <span className="text-sm">{item.name}</span>
                  <span className="text-sm font-medium text-yellow-600">
                    {item.quantity} / {item.reorder_level}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-green-600 font-medium">All stock levels OK</p>
            </div>
          )}
        </div>

        {/* Teller Performance */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <PieChart className="h-4 w-4 text-primary" />
            Teller Performance
            <Link to="/dfs/teller-accounts" className="ml-auto text-xs text-primary hover:underline flex items-center gap-1">
              Details <ChevronRight className="h-3 w-3" />
            </Link>
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {tellerPerf.length > 0 ? (
              tellerPerf.map((teller) => (
                <TellerPerformanceCard key={teller.id} teller={teller} />
              ))
            ) : (
              <p className="text-center text-muted-foreground py-4">No teller data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Transaction History - {new Date(selectedDate).toLocaleDateString()}
          </h3>
          <span className="text-sm text-muted-foreground">{transactions.length} transactions</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left py-2 px-3 font-medium">Time</th>
                <th className="text-left py-2 px-3 font-medium">Type</th>
                <th className="text-left py-2 px-3 font-medium">Description</th>
                <th className="text-right py-2 px-3 font-medium">Amount</th>
                <th className="text-left py-2 px-3 font-medium">By</th>
                <th className="text-left py-2 px-3 font-medium">Reference</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length > 0 ? (
                transactions.map((txn) => (
                  <TransactionRow key={txn.id} transaction={txn} />
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-muted-foreground">
                    No transactions for this date
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Cash Account Modal */}
      <AddCashAccountModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleRefresh}
        companyId={companyId}
        defaultType={addModalType}
      />
    </div>
  );
}
